import type { Me as UserManagementMe, Workspace } from '@micronet/user-management';
import { buildMembersAuthHeaders } from './membersAuthHeaders';
import { ENV } from '../config/env';

/** core's GET /me payload, camelCased. */
export interface Me {
  userId: number;
  tenantId: string;
  tenantOwner: boolean;
  /** IDs of /me's `workspaces`: the ones the caller may act inside, still active. */
  workspaceIds: number[];
  /**
   * /me's `workspaces` as sent, for UserManagementProvider's catalogue — App.tsx
   * lends it this response so the page asks core once (Bug 2916).
   */
  workspaces: Workspace[];
}

/**
 * Reads the caller's identity from core's `GET /me` through the gateway.
 * Returns null when the session does not resolve (the shape `verifySession`
 * reports as "not signed in").
 *
 * `workspaces` is the only server-side answer to "which workspace am I in".
 * Without it the SPA falls back to ENV.workspaceId, which is a build-time
 * constant defaulting to 1 — see useStore.init.
 */
export async function fetchMe(
  getAccessToken: (forceRefresh?: boolean) => Promise<string>
): Promise<Me | null> {
  const token = await getAccessToken();
  const headers = buildMembersAuthHeaders(token, ENV.authzUserId, ENV.workspaceId);
  const res = await fetch(`${ENV.coreApi}/me`, { headers });
  if (!res.ok) return null;
  // A 200 is a resolved session even if its body is empty or not JSON.
  const body = (await res.json().catch(() => ({}))) as {
    user_id?: number;
    tenant_id?: string;
    tenant_owner?: boolean;
    workspaces?: Workspace[] | null;
  };
  // A null (or absent) list is treated as empty rather than letting it reach
  // callers that iterate.
  const workspaces = Array.isArray(body.workspaces) ? body.workspaces : [];
  return {
    userId: body.user_id ?? 0,
    tenantId: body.tenant_id ?? '',
    // Only an explicit true counts, as in user-management's own getMe.
    tenantOwner: body.tenant_owner === true,
    workspaceIds: workspaces.map((ws) => ws.id),
    workspaces,
  };
}

// One /me per tab: the route gate (verifySession) and the store's init both read
// this promise. Memory only, so nothing tenant-scoped outlives the page.
let cached: Promise<Me | null> | null = null;

/**
 * fetchMe, memoised. `force` refetches.
 * A null or failed result is not kept, so the next caller asks again.
 */
export function loadMe(
  getAccessToken: (forceRefresh?: boolean) => Promise<string>,
  force = false
): Promise<Me | null> {
  if (cached && !force) return cached;
  const p = fetchMe(getAccessToken).then(
    (me) => {
      if (me === null && cached === p) cached = null;
      return me;
    },
    (err: unknown) => {
      if (cached === p) cached = null;
      throw err;
    }
  );
  cached = p;
  return p;
}

/**
 * loadMe in UserManagementProvider's `Me` shape, for its `loadMe` prop (Bug 2916).
 * `refresh` (after creating or deleting a workspace) forces a refetch, which also
 * updates the copy verifySession and init share. null — the session did not
 * resolve — lets the provider ask core itself and report the failure.
 */
export async function loadUserManagementMe(
  getAccessToken: (forceRefresh?: boolean) => Promise<string>,
  refresh: boolean
): Promise<UserManagementMe | null> {
  const me = await loadMe(getAccessToken, refresh);
  return (
    me && {
      user_id: me.userId,
      tenant_id: me.tenantId,
      tenant_owner: me.tenantOwner,
      workspaces: me.workspaces,
    }
  );
}

/** Drops the cached identity, so the next caller asks core again. */
export function clearMe(): void {
  cached = null;
}
